//=================================================
// Application bundle
//=================================================
/* eslint no-console:0 */

// Import javascript modules
import { adjustLinkRails, navigationClose, setupNavigation } from '../javascript/modules/navigation';
import { setupAddToCalendar } from '../javascript/modules/add-to-calendar';
import { setupAudio } from '../javascript/modules/audio';
import { setupCards } from '../javascript/modules/cards';
import {
    setupCollectionExpandables,
    setupCollectionSearch,
    setupCollectionStickyTitle,
    setupSyncs
} from '../javascript/modules/collection';
import { setupUserPreferences } from '../javascript/modules/user-preferences';
import { setupExhibitionFeatures, setupExhibitions, setupScrollGradients } from '../javascript/modules/exhibitions';
import { adjustExpandables, setupExpandables } from '../javascript/modules/expandables';
import setupCalendar, { calendarFiltersClose } from '../javascript/modules/calendar';
import setupCarousels from '../javascript/modules/carousel';
import setupCopyable from '../javascript/modules/copyable';
import setupDayPickers from '../javascript/modules/day-picker';
import setupFootnotes, { adjustFootnotes } from '../javascript/modules/footnotes';
import setupJumplinks from '../javascript/modules/jumplinks';
import { getAllFields, replaceHistory, setupLists } from '../javascript/modules/lists';
import setupLocaleSwitches from '../javascript/modules/locale-switch';
import setupMailchimp from '../javascript/modules/mailchimp';
import setupOnTheHour from '../javascript/modules/on-the-hour';
import setupOverlays from '../javascript/modules/overlays';
import setupShuffle, { adjustShuffleImageHeight } from '../javascript/modules/shuffle';
import setupSliders from '../javascript/modules/sliders';
import setupStrobe from '../javascript/modules/strobe';
import setupTabFocus from '../javascript/modules/accessibility';
import setupTicketSelector from '../javascript/modules/event';
import { setupVideos, resizeVideos } from '../javascript/modules/videos';
import {
    adjustSticky,
    createInitializeOnce,
    queryParameters,
    scrollToElementIfNecessary,
    setupViewCounts,
    showFlashAlert,
} from '../javascript/modules/utilities';
import { setupW } from '../javascript/modules/w';
import { setupGuides, stopOverlayClose } from '../javascript/modules/guides';
import { setupAccordions } from '../javascript/modules/accordions';
import { hideAllTooltips, setupTooltips } from '../javascript/modules/tooltips';
import { createConsumer } from '@rails/actioncable';
import { adjustImageViewers, setupImageViewers } from '../javascript/modules/images';
import setupB26Footer, { setupB26Cursor } from '../javascript/modules/biennial-2026-intervention';

const initializeOnce = createInitializeOnce();

function initializeComponents(_e) {
    // Each setup isolated so one component throwing an error doesn't block
    [
        setupUserPreferences, // Set global settings like dark and prefers-reduced-motion
        setupAccordions,
        setupAddToCalendar,
        setupAudio,
        setupB26Cursor,
        setupB26Footer,
        setupCards, // MUST be before adjusting expandables
        setupCollectionExpandables,
        setupCollectionSearch,
        setupCollectionStickyTitle,
        setupSyncs,
        setupCopyable,
        setupDisabledFrameLinks,
        setupDayPickers,
        setupExhibitions,
        setupExpandables,
        setupCalendar,
        setupFootnotes,
        setupGuides,
        setupImageViewers,
        setupJumplinks,
        setupLists,
        setupLocaleSwitches,
        setupMailchimp,
        setupNavigation,
        setupOnTheHour,
        setupOverlays,
        setupCarousels,
        setupScrollGradients,
        setupSliders,
        setupShuffle,
        setupStrobe,
        setupTabFocus,
        setupTicketSelector,
        setupTooltips,
        setupW,
        setupVideos,
        setupViewCounts,

        adjustSticky,
        navigationClose, // Close navigation once page has loaded, any earlier and it feels off
        adjustLinkRails,
    ].forEach((setup) => {
        try {
            setup();
        } catch (error) {
            console.error(`Error initializing ${ setup.name }`, error);
        }
    });

    // artport links can open in windows of exact size (no perfect way to do this)
    document.querySelectorAll('.btn--artport').forEach((el) => {
        if (initializeOnce(el)) {
            el.addEventListener('click', (e) => {
                const { width, height } = el.dataset;
                if (width && height) {
                    e.preventDefault();
                    window.open(el.href, '', `width=${ width },height=${ height }`);
                }
            });
        }
    });

    // Misc analytics tracking across site
    if (useAnalytics) {
        // Ad blocker tracking (once per session; initializeComponents re-runs constantly)
        if (!sessionStorage.getItem('adBlockerTracked')) {
            sessionStorage.setItem('adBlockerTracked', '1');
            const ad = document.createElement('ins');
            ad.className = 'AdSense';
            Object.assign(ad.style, {
                display: 'block',
                position: 'absolute',
                top: '-1px',
                height: '1px',
                opacity: 0,
            });
            document.body.appendChild(ad);
            dataLayer.push({
                event: 'ad_blocker',
                label: 'ad blocker',
                value: !ad.clientHeight,
            });
            ad.remove();
        }

        // SEO related tracking suggested by Hudson Creative
        // Email clicks
        document.querySelectorAll('a[href^="mailto:"]').forEach((el) => {
            if (initializeOnce(el)) {
                el.addEventListener('click', () => {
                    dataLayer.push({
                        event: 'email_click',
                        value: el.href.replace('mailto:', ''),
                    });
                });
            }
        });

        // Maps clicks (both Google and Apple appear to start with maps.[domain]
        document.querySelectorAll('a[href^="https://maps."]').forEach((el) => {
            if (initializeOnce(el)) {
                el.addEventListener('click', () => {
                    dataLayer.push({
                        event: 'maps_click',
                        value: el.href,
                    });
                });
            }
        });

        // Phone number clicks
        document.querySelectorAll('a[href^="tel:"]').forEach((el) => {
            if (initializeOnce(el)) {
                el.addEventListener('click', () => {
                    dataLayer.push({
                        event: 'phone_click',
                        value: el.href.replace('tel:', ''),
                    });
                });
            }
        });
    }

    // Admin live/local links
    document.querySelectorAll('.admin-live-link').forEach((el) => {
        if (initializeOnce(el)) {
            el.addEventListener('click', () => {
                el.href = `https://whitney.org${ window.location.pathname }${ window.location.search }`;
            });
        }
    });

    document.querySelectorAll('.admin-local-link').forEach((el) => {
        if (initializeOnce(el)) {
            el.addEventListener('click', () => {
                el.href = `http://localhost:3000${ window.location.pathname }${ window.location.search }`;
            });
        }
    });

    // Admin toolbar
    document.querySelectorAll('.admin-toolbar__hide').forEach((el) => {
        if (initializeOnce(el)) {
            const toolbar = el.closest('.admin-toolbar');
            el.addEventListener('click', () => {
                hideAllTooltips();
                const isHidden = toolbar.classList.toggle('hidden');
                localStorage.setItem('hideToolbar', isHidden);
            });
        }
    });


    // On the Hour previews
    const onTheHourPreviewEls = document.querySelectorAll('.on-the-hour-preview');
    onTheHourPreviewEls.forEach((el) => {
        if (initializeOnce(el)) {
            const submit = el.querySelector('.on-the-hour-preview__submit');
            submit.addEventListener('click', () => {
                let url = el.querySelector('.on-the-hour-preview__url')?.value;
                const date = el.querySelector('.on-the-hour-preview__date')?.value;
                console.log('date', date);
                if (url.includes('?')) {
                    url = `${ url }&on-the-hour=${ date?.length ? date : 'true' }`;
                } else {
                    url = `${ url }?on-the-hour=${ date?.length ? date : 'true' }`;
                }

                submit.setAttribute('href', url);
            });
        }
    });

    // Show any announcements
    const announcement = document.querySelector('.announcement-wrapper');
    if (announcement && initializeOnce(announcement)) {
        const currentTime = Date.now();
        const lastAnnouncement = localStorage.getItem('announcement');
        // Only show if it's the first time within a day if they've previously closed it
        const withinDay = Number.isNaN(parseInt(lastAnnouncement)) || lastAnnouncement < (currentTime - 86400000);
        // Just don't show on guides with audio, things overlap with the design and not worth it
        const activeGuide = document.querySelector('.audio-wrapper--guide.active');
        // Query at call time: the URL changes across Turbo navigations
        const params = queryParameters();

        if ((withinDay || params.announcement === 'true') && !activeGuide) {
            // Set announcement to active, then allow css animations to slide it up
            announcement.style.bottom = `${ -announcement.offsetHeight }px`;
            setTimeout(() => {
                announcement.classList.add('active');
                announcement.style.bottom = null;
            }, 1);

            announcement.querySelector('.announcement__close').addEventListener('click', () => {
                localStorage.setItem('announcement', currentTime);
                announcement.classList.remove('active');
            });
        }
    }

    // artport easter eggs in the console
    const isArtport = window.location.pathname.toLowerCase().indexOf('/artport') > -1;
    if (!sessionStorage.getItem('artportEasterEgg') && !isArtport) {
        const messages = [
            'ʕ •ᴥ•ʔ∫',
            '(=^・^=)',
            '(づ￣ ³￣)づ',
            '༼つ ◕_◕ ༽つ',
        ];

        const colors = [
            'red',
            'orange',
            'magenta',
            'royalblue',
            'cyan',
        ];

        const color1 = colors[Math.floor(Math.random() * colors.length)];
        const color2 = colors[Math.floor(Math.random() * colors.length)];
        console.log(
            `%c${ messages[Math.floor(Math.random() * messages.length)] } https://whitney.org/artport`,
            `color: ${ color1 }; text-shadow: 1px 1px 0 ${ color2 }`,
        );

        sessionStorage.setItem('artportEasterEgg', '1'); // Only show once a session
    }

    if (!sessionStorage.getItem('artportEasterEasterEgg') && isArtport) {
        const messages = [
            'ヽ(´▽`)/',
            '(˶ᵔ ᵕ ᵔ˶)',
        ];

        const colors = [
            'red',
            'orange',
            'magenta',
            'royalblue',
            'cyan',
        ];

        const color1 = colors[Math.floor(Math.random() * colors.length)];
        const color2 = colors[Math.floor(Math.random() * colors.length)];
        console.log(
            `%c${ messages[Math.floor(Math.random() * messages.length)] }`,
            `color: ${ color1 }; text-shadow: 1px 1px 0 ${ color2 }`,
        );

        sessionStorage.setItem('artportEasterEasterEgg', '1'); // Only show once a session
    }
}

document.addEventListener('DOMContentLoaded', (e) => {
    initializeComponents(e);

    // Anything that should only load once is below (e.g. listeners on document)

    // Admin toasts
    const userId = document.querySelector('meta[name="user-id"]')?.getAttribute('content');
    if (userId) {
        const consumer = createConsumer();
        consumer.subscriptions.create({ channel: 'AdminChannel', id: userId }, {
            connected() {
                console.log('WS: connected');
            },
            disconnected() {
                console.warn('WS: disconnected');
            },
            received(response) {
                console.log('WS: response', response);

                if (response.message) {
                    showFlashAlert(response.type, response.message);
                }

                if (response.sync_selector) {
                    document.querySelectorAll(response.sync_selector).forEach((el) => {
                        el.disabled = false;
                        el.classList.remove('syncing');
                    });
                }

                if (response.timestamp_selector) {
                    document.querySelectorAll(response.timestamp_selector).forEach((el) => {
                        el.innerHTML = 'just now';
                    });
                }
            },
            rejected() {
                console.warn('WS: rejected');
            },
        });
    }

    // Global document listeners that should only bind once
    // (initializeComponents re-runs on every turbo render and these anonymous handlers can't be de-duplicated)
    if (initializeOnce(document)) {
        // Escape key handling
        document.addEventListener('keydown', (e) => {
            const primary = document.querySelector('.primary-nav');
            if (primary) {
                if (e.key === 'Escape') {
                    navigationClose();
                    hideAllTooltips();
                }
            }
        });

        // Hide dropdown menu on outside click
        document.addEventListener('click', (e) => {
            const navigation = document.querySelector('.navigation')
            if (navigation) {
                const openNav = navigation.querySelector('.open');
                if (openNav) {
                    const primaryNav = e.target.closest('.primary-nav');
                    const miniMenu = e.target.closest('.mini-header__menu-small');

                    // Only close if the click was outside the nav + nav toggle button or is the small home button
                    if (!primaryNav && !miniMenu) {
                        navigationClose();
                    }
                }
            }
        });
    }
});

// Add the custom data-turbo-target-top attribute to make all descendant links drive a full page load
// This is useful for user-generated rich text content that appears inside Turbo frames
const setupDisabledFrameLinks = () => {
    document.querySelectorAll('turbo-frame [data-turbo-target-top] a[href]').forEach((el) => {
        // Skip links that already have turbo-frame targeting
        if (el.hasAttribute('data-turbo-frame')) {
            return;
        }

        const href = el.getAttribute('href');
        if (href && (href.startsWith('/') || href.startsWith('https://whitney.org'))) {
            el.setAttribute('data-turbo-frame', '_top');
        }
    });

    // Disable turbo on any locally hosted microsites etc
    document.querySelectorAll('a[href^="/www"]').forEach((el) => {
        el.setAttribute('data-turbo', 'false');
    });
};

// Keep Turbo frame faded until its new content renders (removed in turbo:frame-render)
// (Turbo drops aria-busy just before the swap, which is annoying)
document.addEventListener('turbo:before-fetch-request', (e) => {
    if (document.body.classList.contains('admin')) return;
    const frame = e.target.closest?.('turbo-frame');
    if (frame && !frame.classList.contains('no-loading')) {
        frame.classList.add('loading');
    }
});

// Network failures never fire a render event; don't leave the frame faded/busy
document.addEventListener('turbo:fetch-request-error', (e) => {
    const frame = e.target.closest?.('turbo-frame');
    if (frame) {
        frame.classList.remove('loading');
        frame.removeAttribute('aria-busy');
    }
});

document.addEventListener('turbo:render', (e) => {
    // Sometimes Turbo frames get stuck on busy state when landing via back/forward buttons
    document.querySelectorAll('turbo-frame[aria-busy=true], turbo-frame.loading').forEach((el) => {
        el.removeAttribute('aria-busy');
        el.removeAttribute('busy');
        el.removeAttribute('complete');
        el.classList.remove('loading');
    });

    // Re-initialize any components added via Turbo render
    initializeComponents(e);
});

document.addEventListener('turbo:frame-render', (e) => {
    // Remove any loading state now that the content has been rendered
    e.target.classList.remove('loading');

    // Only scroll to the element if the top isn't in view (better than Turbo autoscroll handling)
    // ...and if it's not a turbo frame we want to ignore for UX reasons
    const ignoreSelectors = ['#turbo_exhibition_feature', '#turbo_exhibition_feature_section'];
    const matches = ignoreSelectors.some(selector => e.target.matches(selector));
    if (!matches) {
        scrollToElementIfNecessary(e.target);
    }

    // Re-initialize any new components (like videos)
    initializeComponents(e);
});

// Turbo form handling
document.addEventListener('turbo:submit-start', (e) => {
    // Add aria-busy since it doesn't get added automatically if we're doing turbo stream updates
    e.target.querySelectorAll('turbo-frame').forEach((el) => {
        el.setAttribute('aria-busy', 'true');
    });

    // Update history, should only be used with Turbo Streams (be VERY careful), everything else should be native frames
    if (e.target.closest('.list')?.dataset.history === 'true') {
        const allFields = getAllFields(e.target.closest('.list'));
        allFields.push({ name: 'page', value: null, invert: false });
        replaceHistory(allFields);
    }
});

document.addEventListener('turbo:submit-end', (e) => {
    // Remove loading from any frames or streams
    e.target.querySelectorAll('turbo-frame').forEach((el) => {
        el.removeAttribute('aria-busy');
        el.classList.remove('loading');
    });

    // Observe DOM changes and initialize if anything changes (otherwise things loaded via turbo streams might not update)
    // Watch the body, not the form: turbo streams can target elements anywhere on the page
    const observer = new MutationObserver((mutations) => {
        // Hate this but needs some kind of 0 duration delay otherwise this fires too early
        setTimeout(() => {
            initializeComponents(e);
        }, 0)
        // Disconnect the observer to ensure it only runs once
        observer.disconnect();
    });
    observer.observe(document.body, {
        childList: true,
        subtree: true,
    });
    // If the response changed nothing, don't leave the observer attached forever
    setTimeout(() => observer.disconnect(), 2000);
});

window.addEventListener('resize', () => {
    const breakpoint = 800;
    const breakpointSmall = 650;
    adjustSticky();
    adjustShuffleImageHeight();
    adjustImageViewers();

    // Run certain resize events only when horizontal, not vertical, changes
    const width = window.innerWidth;
    if (width !== window.cacheWidth) {
        window.isNavSmall = width < 800;
        window.isSmall = width < 650;
        window.isMedium = width >= 650 && width < 1000;

        adjustExpandables();
        adjustFootnotes();
        adjustLinkRails();
        resizeVideos();

        // Moving from small nav to large nav
        if ((width >= breakpoint) && (window.cacheWidth < breakpoint)) {
            // Close small nav since the large nav doesn't hide the page background etc.
            navigationClose();
            stopOverlayClose();

            // Re-initialize when viewport crosses into the large breakpoint.
            // This hydrates any feature-sections templates that were skipped on initial load (small-screen)
            // then re-run setup so hover/popup handlers attach to the new DOM.
            setupExhibitionFeatures()
        }

        // Moving from large nav to small nav
        if ((width < breakpoint) && (window.cacheWidth >= breakpoint)) {
            stopOverlayClose();
        }

        // Moving from medium/large screen to small
        if ((width < breakpointSmall) && (window.cacheWidth >= breakpointSmall)) {
            calendarFiltersClose();
        }

        // Moving from small to medium/large
        if ((width >= breakpointSmall) && (window.cacheWidth < breakpointSmall)) {
            calendarFiltersClose();
        }

        // Update cache width once everything is done
        window.cacheWidth = width;
    }
});
